import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { MAX_PAGE_SIZE } from '../../../shared/core/services/pagination.util';
import { fetchAll } from '../../../shared/rx';
import type { CacProvider, CacRattachementQuery, RenouvellementCac } from '../../gouvernance/portefeuille/fiche-360/onglets/cac-provider.port';
import { CacApiService } from './cac-api.service';
import type { MandatCommissaireAuxComptesCreateRequest } from './models/cac-api.model';
import type { MandatDetailleCac, PersonneMoraleRef, RattachementCac, RemplacementCac } from './models/cac.model';
import { CacRattachementApiService } from './services/cac-rattachement-api.service';
import { MandatCommissaireComptesService } from './services/mandat-commissaire-comptes.service';

/**
 * Adaptateur du port `CAC_PROVIDER` : implémente les opérations CAC attendues par la
 * Fiche 360° (Gouvernance) en déléguant aux services réels du module Performance.
 * Câblé au composition root (`app.config`). N'importe le port qu'en `type` : le seul
 * lien runtime Performance ↔ Gouvernance est le binding du token dans `app.config`.
 */
@Injectable()
export class CacProviderAdapter implements CacProvider {
    private readonly cacApi = inject(CacApiService);
    private readonly mandatService = inject(MandatCommissaireComptesService);
    private readonly rattachementService = inject(CacRattachementApiService);

    listCabinets(): Observable<PersonneMoraleRef[]> {
        return this.cacApi.listCabinets();
    }

    listRattachements(query: CacRattachementQuery): Observable<{ results: RattachementCac[] }> {
        // Pagination réelle (backend plafonné à 100/page) : la Fiche 360° attend
        // TOUS les rattachements de la société, pas la 1re page.
        return fetchAll((page) => this.cacApi.listRattachements({ ...query, page, per_page: MAX_PAGE_SIZE }));
    }

    createMandat(payload: MandatCommissaireAuxComptesCreateRequest): Observable<{ id: string }> {
        return this.mandatService.create(payload);
    }

    remplacerCommissaire(payload: RemplacementCac): Observable<unknown> {
        return this.rattachementService.remplacerCac(payload);
    }

    /** Reconduction : `createMandat` relit le mandat d'origine et reposte la paire entière. */
    renouvelerMandat(payload: RenouvellementCac): Observable<unknown> {
        return this.cacApi.createMandat({
            rattachement_id: payload.mandat_id,
            date_debut_mandat: payload.date_debut_mandat,
            reference_acte_nomination: payload.reference_acte_nomination,
        });
    }

    /**
     * Mandats CAC d'une personne, quel que soit son rôle qu'elle y tienne. Le serveur expose
     * `is_titulaire_or_suppleant` depuis la mise à jour d'octobre 2026 : une seule lecture suffit,
     * là où il fallait auparavant interroger chaque rôle séparément.
     */
    listMandatsPersonne(personneId: string): Observable<MandatDetailleCac[]> {
        return this.cacApi.listMandatsDetailles({ is_titulaire_or_suppleant: personneId });
    }
}
